import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { Search, BookOpen, X } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../api/examApi';

export default function AdminClassroomPage() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [facultyFilter, setFacultyFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    adminApi.listClassEntries()
      .then(({ data }) => setEntries(data))
      .catch(() => toast.error('Failed to load class entries'))
      .finally(() => setLoading(false));
  }, []);

  const groupOptions = useMemo(() => {
    const seen = new Map();
    entries.forEach((e) => seen.set(e.group_id, e.group_name));
    return Array.from(seen.entries());
  }, [entries]);

  const facultyOptions = useMemo(() => {
    const seen = new Map();
    entries.forEach((e) => seen.set(e.faculty_id, e.faculty_name));
    return Array.from(seen.entries());
  }, [entries]);

  const filteredEntries = useMemo(() => {
    return entries.filter((e) => {
      if (search && !e.title.toLowerCase().includes(search.toLowerCase())) return false;
      if (groupFilter && e.group_id !== groupFilter) return false;
      if (facultyFilter && e.faculty_id !== facultyFilter) return false;
      return true;
    });
  }, [entries, search, groupFilter, facultyFilter]);

  const clearFilters = () => {
    setSearch('');
    setGroupFilter('');
    setFacultyFilter('');
  };

  return (
    <DashboardLayout title="Classroom Oversight">
      <div className="animate-fade-in space-y-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">Classroom Oversight</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            All class entries across every group and faculty member — read-only.
          </p>
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title..."
                className="w-full pl-10 pr-4 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
                data-testid="group-filter"
              >
                <option value="">All Groups</option>
                {groupOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
              <select
                value={facultyFilter}
                onChange={(e) => setFacultyFilter(e.target.value)}
                className="px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool focus:border-transparent transition-colors"
                data-testid="faculty-filter"
              >
                <option value="">All Faculty</option>
                {facultyOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
              {(search || groupFilter || facultyFilter) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="p-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                  data-testid="clear-filters-btn"
                  title="Clear filters"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
          {loading ? (
            <div className="animate-pulse">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center p-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                </div>
              ))}
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
              <BookOpen size={28} className="text-slate-300 dark:text-slate-600" />
              No class entries found.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Title</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Group</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Faculty</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Date</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Files</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredEntries.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => navigate(`/admin/classroom/${e.id}`)}
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer"
                    data-testid={`class-entry-row-${e.id}`}
                  >
                    <td className="px-5 py-3">
                      <p className="font-medium text-slate-900 dark:text-white">{e.title}</p>
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{e.group_name}</td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{e.faculty_name}</td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                      {new Date(`${e.date}T00:00:00`).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{e.files?.length ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
